import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Fade,
  Alert,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import { startDailySession, endDailySession, checkActiveDailySession } from "../../../services/Settings/ServicesandTaxSettingsAndSessios/SessoinAndShiftsApi";

const DailySessions = () => {
  const [loading, setLoading] = useState(false);
  const [sessionActive, setSessionActive] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  // ✅ كشف دقيق لجميع أحجام التابلت
  const isSmallTablet = useMediaQuery("(min-width: 600px) and (max-width: 768px)");
  const isMediumTablet = useMediaQuery("(min-width: 769px) and (max-width: 900px)");
  const isLargeTablet = useMediaQuery("(min-width: 901px) and (max-width: 1024px)");
  const isTablet = useMediaQuery("(min-width: 600px) and (max-width: 1024px)");
  
  // ✅ كشف الوضع الأفقي/العمودي
  const isPortrait = useMediaQuery("(orientation: portrait)");
  const isLandscape = useMediaQuery("(orientation: landscape)");

  // ✅ حساب أحجام العناصر حسب حجم الشاشة
  const getIconSize = () => {
    if (isSmallTablet) return 24;
    if (isMediumTablet) return 26;
    return 28;
  };

  const getTitleSize = () => {
    if (isSmallTablet) return "16px";
    if (isMediumTablet) return "18px";
    return "20px";
  };

  const getStatusIconSize = () => {
    if (isSmallTablet) return 18;
    if (isMediumTablet) return 19;
    return 20;
  };

  const getButtonFontSize = () => {
    if (isSmallTablet) return "14px";
    if (isMediumTablet) return "15px";
    return "16px";
  };

  const getButtonPadding = () => {
    if (isSmallTablet) return 1;
    if (isMediumTablet) return 1.1;
    return 1.2;
  };

  const getBoxPadding = () => {
    if (isSmallTablet) return 2;
    if (isMediumTablet) return 2.5;
    return 3;
  };

  const getBoxGap = () => {
    if (isSmallTablet) return 1.5;
    if (isMediumTablet) return 2;
    return 2.5;
  };

  useEffect(() => {
    const fetchSessionStatus = async () => {
      setLoading(true);
      try {
        const status = await checkActiveDailySession();
        setSessionActive(status === "active");
      } catch (error) {
        console.error("❌ فشل في جلب حالة اليومية:", error.message);
        setErrorMsg("فشل في جلب حالة اليومية");
      } finally {
        setLoading(false);
      }
    };

    fetchSessionStatus();
  }, []);

  // ✅ إذا لم يكن تابلت، لا تعرض المكون
  if (!isTablet) {
    return null;
  }

  const handleStartSession = async () => {
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      await startDailySession();
      setSuccessMsg("تم تفعيل اليومية بنجاح ✅");
      setSessionActive(true);
      setTimeout(() => setSuccessMsg(""), 3000);
    } catch (error) {
      setErrorMsg("فشل في تفعيل اليومية ❌");
      setTimeout(() => setErrorMsg(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleEndSession = async () => {
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");
    try {
      await endDailySession();
      setSuccessMsg("تم إيقاف اليومية بنجاح ✅");
      setSessionActive(false);

      await new Promise((resolve) => setTimeout(resolve, 2000));

      try {
        await startDailySession();
        setSuccessMsg("تم بدء يومية جديدة بنجاح ✅");
        setSessionActive(true);
        setTimeout(() => setSuccessMsg(""), 3000);
      } catch (startErr) {
        console.error("❌ فشل في إعادة تفعيل اليومية:", startErr);
        setErrorMsg("فشل في إعادة تفعيل اليومية بعد الإيقاف ❌");
        setTimeout(() => setErrorMsg(""), 3000);
      }
    } catch (error) {
      setErrorMsg("فشل في إيقاف اليومية ❌");
      setTimeout(() => setErrorMsg(""), 3000);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in={true} timeout={300}>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={getBoxGap()}
        sx={{
          p: getBoxPadding(),
          borderRadius: isSmallTablet ? 2 : 3,
          boxShadow: isDark
            ? "0 8px 24px rgba(0,0,0,0.4)"
            : "0 8px 24px rgba(0,0,0,0.08)",
          background: isDark
            ? "linear-gradient(135deg, #1c1c2b 0%, #26334d 100%)"
            : "linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%)",
          color: isDark ? "#fff" : "#232334",
          border: isDark ? "1px solid #333" : "1px solid #eee",
          transition: "all 0.3s ease",
          // ✅ تحسين للأداء
          willChange: "transform",
          // ✅ دعم safe area
          maxWidth: "100%",
          minHeight: isSmallTablet ? 280 : isMediumTablet ? 320 : 360,
          // ✅ تحسين touch targets
          "& .MuiButton-root": {
            minHeight: 44,
            touchAction: "manipulation",
          },
        }}
      >
        {/* Header مع أيقونة */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isSmallTablet ? 1 : 1.5,
            width: "100%",
            justifyContent: "center",
            flexWrap: "wrap",
          }}
        >
          <AccessTimeIcon
            sx={{
              fontSize: getIconSize(),
              color: isDark ? "#f48fb1" : "#1976d2",
              animation: sessionActive ? "pulse 1.5s infinite" : "none",
              "@keyframes pulse": {
                "0%, 100%": { opacity: 1 },
                "50%": { opacity: 0.6 },
              },
              transition: "all 0.3s ease",
            }}
          />
          <Typography
            variant="h6"
            fontWeight={700}
            sx={{
              color: isDark ? "#f48fb1" : "#1976d2",
              fontSize: getTitleSize(),
              textAlign: "center",
              lineHeight: 1.3,
            }}
          >
            إدارة اليومية
          </Typography>
        </Box>

        {/* Status Indicator */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: isSmallTablet ? 0.8 : 1,
            px: isSmallTablet ? 1.5 : 2,
            py: isSmallTablet ? 0.8 : 1,
            borderRadius: isSmallTablet ? 1.5 : 2,
            bgcolor: sessionActive
              ? isDark
                ? "rgba(76, 175, 80, 0.15)"
                : "rgba(76, 175, 80, 0.08)"
              : isDark
              ? "rgba(255, 152, 0, 0.15)"
              : "rgba(255, 152, 0, 0.08)",
            border: `${isSmallTablet ? 1 : 1.5}px solid ${
              sessionActive
                ? isDark
                  ? "rgba(76, 175, 80, 0.3)"
                  : "rgba(76, 175, 80, 0.2)"
                : isDark
                ? "rgba(255, 152, 0, 0.3)"
                : "rgba(255, 152, 0, 0.2)"
            }`,
            width: "100%",
            justifyContent: "center",
            transition: "all 0.3s ease",
            "&:hover": {
              transform: "scale(1.02)",
            },
          }}
        >
          {sessionActive ? (
            <>
              <CheckCircleIcon
                sx={{
                  color: "#4caf50",
                  fontSize: getStatusIconSize(),
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600, 
                  color: "#4caf50",
                  fontSize: isSmallTablet ? "13px" : "14px",
                }}
              >
                اليومية مفعّلة
              </Typography>
            </>
          ) : (
            <>
              <ErrorIcon
                sx={{
                  color: "#ff9800",
                  fontSize: getStatusIconSize(),
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  fontWeight: 600, 
                  color: "#ff9800",
                  fontSize: isSmallTablet ? "13px" : "14px",
                }}
              >
                اليومية معطّلة
              </Typography>
            </>
          )}
        </Box>

        {/* Messages */}
        {successMsg && (
          <Alert 
            severity="success" 
            sx={{ 
              width: "100%", 
              borderRadius: isSmallTablet ? 1.5 : 2,
              fontSize: isSmallTablet ? "12px" : "14px",
              "& .MuiAlert-icon": {
                fontSize: isSmallTablet ? 18 : 20,
              },
            }}
          >
            {successMsg}
          </Alert>
        )}
        {errorMsg && (
          <Alert 
            severity="error" 
            sx={{ 
              width: "100%", 
              borderRadius: isSmallTablet ? 1.5 : 2,
              fontSize: isSmallTablet ? "12px" : "14px",
              "& .MuiAlert-icon": {
                fontSize: isSmallTablet ? 18 : 20,
              },
            }}
          >
            {errorMsg}
          </Alert>
        )}

        {/* Button */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 2,
            }}
          >
            <CircularProgress
              size={isSmallTablet ? 40 : isMediumTablet ? 44 : 48}
              sx={{
                color: isDark ? "#f48fb1" : "#1976d2",
              }}
            />
          </Box>
        ) : sessionActive ? (
          <Button
            variant="contained"
            color="error"
            onClick={handleEndSession}
            fullWidth
            sx={{
              fontWeight: 600,
              borderRadius: isSmallTablet ? 1.5 : 2,
              py: getButtonPadding(),
              fontSize: getButtonFontSize(),
              bgcolor: isDark ? "#d32f2f" : "#f44336",
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              minHeight: 44,
              textTransform: "none",
              boxShadow: "0 2px 8px rgba(244, 67, 54, 0.2)",
              "&:hover": {
                bgcolor: isDark ? "#b71c1c" : "#e53935",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(244, 67, 54, 0.3)",
              },
              "&:active": {
                transform: "translateY(0px)",
                boxShadow: "0 2px 6px rgba(244, 67, 54, 0.2)",
              },
            }}
          >
            إيقاف اليومية
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            onClick={handleStartSession}
            fullWidth
            sx={{
              fontWeight: 600,
              borderRadius: isSmallTablet ? 1.5 : 2,
              py: getButtonPadding(),
              fontSize: getButtonFontSize(),
              bgcolor: isDark ? "#388e3c" : "#4caf50",
              transition: "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
              minHeight: 44,
              textTransform: "none",
              boxShadow: "0 2px 8px rgba(76, 175, 80, 0.2)",
              "&:hover": {
                bgcolor: isDark ? "#2e7d32" : "#43a047",
                transform: "translateY(-2px)",
                boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
              },
              "&:active": {
                transform: "translateY(0px)",
                boxShadow: "0 2px 6px rgba(76, 175, 80, 0.2)",
              },
            }}
          >
            تفعيل اليومية
          </Button>
        )}

        {/* Helper Text */}
        <Typography
          variant="caption"
          sx={{
            color: isDark ? "#999" : "#666",
            fontSize: isSmallTablet ? "11px" : "12px",
            textAlign: "center",
            lineHeight: 1.4,
            px: 1,
            maxWidth: "100%",
          }}
        >
          {sessionActive
            ? "اليومية نشطة الآن - يمكنك إيقافها وإنشاء يومية جديدة"
            : "قم بتفعيل اليومية لبدء العمل"}
        </Typography>
      </Box>
    </Fade>
  );
};

export default DailySessions;

